<template>

    <div class="canvas::editor-website-editors-input-IconInput">
        <accordion-input :accordion="accordion" :spacing="spacing" :open="open">
            <template #label>

                <div class="font-medium">{{ label }}</div>

            </template>
            <template #content>

                <small v-if="instruction" class="block mb-3 text-gray-500 text-muted">
                    {{ instruction }}
                </small>

                <div class="mt-1">

                    <SwitchInput
                        v-if="showExtraInputs"
                        label="Toon icoon"
                        instruction=""
                        :options="options"
                        options-property="showIcon"
                        :items="[
                            { value: '0', label: 'Uit'},
                            { value: '1', label: 'Aan'},
                        ]"
                        defaultValue="1"
                        :accordion="false"
                        :spacing="{'horizontal': false, 'vertical': true}"
                    />

                    <div class="flex">
                        <input type="search" 
                            class="w-full px-3 py-2 mt-1 transition duration-150 ease-in-out border border-gray-300 border-solid rounded-l-md focus:border-blue-300 focus:outline-none focus:shadow-outline-blue form-control form-input sm:leading-5 sm:text-sm" 
                            placeholder="Zoek een icoon" 
                            @input="updateIconSearch($event)" 
                        >

                        <select class="flex-shrink-0 w-24 px-3 py-2 mt-1 transition duration-150 ease-in-out border border-gray-300 border-solid rounded-r-md focus:border-blue-300 focus:outline-none focus:shadow-outline-blue form-control form-input sm:leading-5 sm:text-sm" 
                            @change="setFontWeight($event)"
                        >
                            <option value="">Alles</option>
                            <option v-for="weight in ['thin', 'solid', 'regular', 'light', 'duotone', 'brands']" 
                                :key="weight" 
                                :value="weight"
                                :selected="weight === iconFontWeight"
                            >{{ weight }}</option>
                        </select>
                    </div>
                    
                    <div class="px-4 py-2 mb-2 bg-[#f1f5f9] mt-2 rounded-md">
                        <span>Geselecteerd: </span>
                        <i :class="{
                            ['fa-' + (iconSelected.styles.includes(iconFontWeight) ? iconFontWeight : (iconSelected.styles[0] || 'solid'))]: true,
                            ['fa-fw']: true,
                            ['fa-' + iconSelected.name]: true,
                        }" v-if="iconSelected"></i> 
                        <span v-if="iconSelected">{{ iconSelected.label }}</span>
                    </div>

                    <div class="py-2 overflow-y-hidden" ref="iconWrapper">
                        
                        <div ref="iconContainer" class="grid items-start h-64 grid-cols-3 gap-6 overflow-y-auto resize-y"
                            :style="dynamicHeight"
                        >
                            <div v-for="(icon, name) in displayedIcons" :key="icon.id"
                                class="flex flex-col items-center justify-center h-20 p-2 rounded-md cursor-pointer hover:bg-gray-200"
                                :class="{
                                    'bg-indigo-400 hover:bg-indigo-400 text-white': selected = iconSelected && iconSelected.label === icon.label,
                                    'bg-gray-100': !iconSelected || iconSelected.label !== icon.label,
                                    //'bg-red-50': !selected && iconFontWeight && !icon.styles.includes(iconFontWeight),
                                }"
                                v-on:click="selectIcon(icon)"
                            >
                                <div class="inline-flex items-center justify-center w-12 h-12 text-center">
                                    <i :class="{
                                        ['fa-' + (icon.styles.includes(iconFontWeight) ? iconFontWeight : (icon.styles[0] || 'solid'))]: true,
                                        ['fa-fw']: true,
                                        ['fa-' + icon.name]: true,
                                    }"></i>
                                </div>
                                <div class="text-xs text-center">{{ icon.label }}</div>
                            </div>
                        </div>

                    </div>

                    <div class="flex items-center gap-4" v-if="showIconPosition && showExtraInputs">
                        <span class="flex-shrink-0 mt-1 text-gray-500 text-muted">Positie</span>
                        
                        <select v-model="options[optionsProperty + '_position']" class="flex-1 px-3 py-2 mt-1 transition duration-150 ease-in-out border border-gray-300 border-solid rounded-md focus:border-blue-300 focus:outline-none focus:shadow-outline-blue form-control form-input sm:leading-5 sm:text-sm">
                            <option value="left" selected>Links</option>
                            <option value="right">Rechts</option>
                        </select>
                    </div>

                </div>

            </template>
        </accordion-input>
    </div>

</template>

<script>

import AccordionInput from "@galaxy/modules/Canvas/resources/js/editor/website/editors/inputs/helpers/Input";
import FontAwesomeYaml from "@fortawesome/fontawesome-pro/metadata/icons.yml";
import YAML from "js-yaml";
import SwitchInput from "@galaxy/modules/Canvas/resources/js/editor/website/editors/inputs/SwitchInput";

export default {

    components: {
        AccordionInput,
        SwitchInput,
    },

    props: {

        label: {
            type: String,
            required: true,
        },
        instruction: String,

        spacing: {
            horizontal: {
                type: Boolean,
                default: true
            },
            vertical: {
                type: Boolean,
                default: true
            }
        },

        accordion: {
            type: Boolean,
            default: true
        },

        open: {
            type: Boolean,
            default: null
        },

        options: {
            type: Object,
            required: true,
        },
        
        optionsProperty: {
            required: true,
        },

        showIconPosition: {
            type: Boolean,
            default: false,
        },

        colors: {
            type: Array,
        },

        defaultColor: {},

        showExtraInputs: {
            type: Boolean,
            default: true,
        },

        dispatchEventName: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            isOpen: this.open || false,
            searchTimeout: null,
            iconSearch: '',
            iconFontWeight: '',
            iconSelected: null,
            allIcons: {}, // All icons loaded from YAML
            icons: {}, // All icons loaded from YAML
            displayedIcons: [], // Icons currently displayed
            itemsPerPage: 24, // Number of icons to load per batch
            currentPage: 1, // Track current page of items
        };
    },


    async created()
    {
        await this.loadFontawesomeIcons();
        
        // Debounce the method
        this.debouncedSearch = _.debounce(this.updateIconSearch, 300);
    },

    mounted() {
        this.$refs.iconContainer.addEventListener('scroll', this.handleScroll);
        this.setIconSelected();
    },
    
    beforeDestroy() {
        this.$refs.iconContainer.removeEventListener('scroll', this.handleScroll);
    },

    methods: {

        selectIcon(icon)
        {
            this.iconSelected = icon;

            // Check if the icon has the selected font weight. If not, use the first available.
            let iconFontWeight = icon.styles.includes(this.iconFontWeight) ? this.iconFontWeight : (icon.styles[0] || 'solid');
            this.iconFontWeight = iconFontWeight;

            this.setValue(`<i class="fa-${icon.name} fa-${iconFontWeight} fa-fw"></i>`);
        },

        setIconSelected()
        {
            let markup = this.options.icon || '';
            let iconName = this.extractIconName(markup);

            // Search for the icon in the list. Guard: a stored icon may not
            // exist in the current Font Awesome set (renamed/removed, a brand,
            // or simply a different markup format) — find() then returns
            // undefined and we must not set properties on it.
            if (iconName) {
                this.iconSelected = this.allIcons.find(icon => icon.name === iconName) || null;

                if (this.iconSelected) {
                    let styles = this.iconSelected.styles || [];
                    let weight = this.extractIconWeight(markup);

                    this.iconSelected.fontWeight = (weight && styles.includes(weight))
                        ? weight
                        : (styles[0] || 'solid');
                    this.iconFontWeight = this.iconSelected.fontWeight;
                }
            }

            this.emitIconData(); // Emit updated data
        },

        setValue(value) {
            // Set in options.
            this.$set(this.options, this.optionsProperty, value);

            // Emit for whoever wants to know.
            this.$emit('iconChange');
            this.emitIconData(); // Emit updated data
        },

        async loadFontawesomeIcons() {
            try {
                const allIcons = YAML.load(FontAwesomeYaml);
                this.allIcons = Object.entries(allIcons).map(([name, icon]) => ({ ...icon, name }));
                this.icons = Object.entries(allIcons).map(([name, icon]) => ({ ...icon, name }));
                this.loadMoreIcons(); // Load first batch
            } catch (e) {
                console.error("Error reading YAML file:", e);
            }
        },

        loadMoreIcons() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;

            // Paginate only filtered icons
            const newIcons = this.filteredIcons.slice(start, end);

            this.displayedIcons.push(...newIcons);
            this.currentPage++;
        },

        refreshIcons() {
            // Reset displayed icons & pagination
            this.displayedIcons = [];
            this.currentPage = 1;

            // Reload paginated results
            this.loadMoreIcons();
        },

        handleScroll(event) {
            const target = event.target;
    
            // Calculate if we're near the bottom
            const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 100;

            
            if (nearBottom) {
                this.loadMoreIcons();
            }
        },

        extractIconName(classString) {
            if (!classString) return null;

            // Accept either a bare class string or a full <i class="…"> tag —
            // pull just the class attribute so a trailing "></i> can't glue
            // itself onto the last class name.
            let match = classString.match(/class=["']([^"']*)["']/);
            let classes = (match ? match[1] : classString).split(/\s+/);

            // The icon NAME is the `fa-…` class that isn't a style/utility
            // class (fa-solid, fa-regular, fa-fw, …) — those describe weight.
            let styleClasses = ['fa-solid', 'fa-regular', 'fa-light', 'fa-thin', 'fa-duotone', 'fa-brands', 'fa-sharp', 'fa-fw'];
            for (let cls of classes) {
                if (cls.startsWith('fa-') && !styleClasses.includes(cls)) {
                    return cls.slice(3);
                }
            }
            return null;
        },

        extractIconWeight(classString) {
            if (!classString) return null;

            let match = classString.match(/class=["']([^"']*)["']/);
            let classes = (match ? match[1] : classString).split(/\s+/);

            // FA5 shorthand (fas/far/…) and FA6 style classes (fa-solid/…).
            let fa5 = { fas: 'solid', far: 'regular', fal: 'light', fat: 'thin', fad: 'duotone', fab: 'brands' };
            let fa6 = ['solid', 'regular', 'light', 'thin', 'duotone', 'brands', 'sharp'];
            for (let cls of classes) {
                if (fa5[cls]) return fa5[cls];
                if (cls.startsWith('fa-') && fa6.includes(cls.slice(3))) return cls.slice(3);
            }
            return null;
        },

        setFontWeight(event) {
            this.iconFontWeight = event.target.value;

            if( this.iconSelected ) {
                this.selectIcon(this.iconSelected);
            }

            this.emitIconData(); // Emit updated data
        },

        updateIconSearch(event) {
            this.iconSearch = event.target.value;
            this.refreshIcons(); // Reload search results with pagination
        },

        emitIconData() {
            let dispatchEventName = this.dispatchEventName;

            if(!this.dispatchEventName) {
                dispatchEventName = 'field-' + this.optionsProperty + '-icon-selected';
            } else if(this.dispatchEventName.startsWith('#')) {
                // Get closts parent using the ID
                dispatchEventName = this.$el.parentNode.querySelector(this.dispatchEventName).textContent;
            }

            // Emit an event with all relevant data
            window.dispatchEvent(new CustomEvent(dispatchEventName, {
                detail: {
                    name: this.iconSelected?.name || '',
                    value: this.options[this.optionsProperty] || '',
                    style: this.iconFontWeight || 'solid',
                    showIcon: this.options[this.optionsProperty + '_showIcon'] || '1',
                    position: this.options[this.optionsProperty + '_position'] || 'left',
                    unicode: this.iconSelected?.unicode || '',
                },
            }));
        },

    },

    computed: {
        filteredIcons() {
            if (!this.iconSearch.trim()) return this.allIcons; // Return all icons if no search input

            const searchTerms = this.iconSearch.toLowerCase().trim().split(/\s+/); // Split search input into words

            // Map through icons and calculate match scores
            let matchingIcons = this.allIcons
                .map(icon => {
                    // Convert icon properties into a single searchable string
                    const iconData = [
                        icon.label.toLowerCase(),
                        icon.name.toLowerCase(),
                        ...(icon.search?.terms?.map(term => term.toLowerCase()) || [])
                    ].join(' ');

                    // Count how many search terms exist in the icon data
                    const matchCount = searchTerms.reduce((count, term) => {
                        return iconData.includes(term) ? count + 1 : count;
                    }, 0);

                    return { icon, matchCount };
                })
                .filter(entry => entry.matchCount > 0) // Remove non-matching results
                .sort((a, b) => b.matchCount - a.matchCount) // Sort by match count (descending)
                .map(entry => entry.icon); // Extract sorted icons

            return matchingIcons;
        },

        dynamicHeight() {
            const itemCount = this.displayedIcons.length;
            const rowCount = Math.ceil(itemCount / 3); // 3 columns per row
            const rowHeight = 100; // Approx height per row (adjust as needed)
            const minHeight = 150; // Minimum height
            const maxHeight = 256; // Maximum default height before scrolling

            // Calculate height dynamically
            let height = rowCount * rowHeight;

            // Ensure it stays within min & max limits
            height = Math.max(minHeight, Math.min(height, maxHeight));

            return {
                height: `${height}px`,
                minHeight: `${minHeight}px`,
                maxHeight: '500px', // Users can manually expand up to this
            };
        },
    },

}

</script>